/**
 * This is a partial port of the angular Location service with noted adapations to work in a React context.
 * The most notable adaption is that all storage and management of the actual current Location/Region
 * is handled by the location.context.js rather than in this helper file.
 */

import axios from 'axios';
import Geocode from 'react-geocode';
import { each, find, mapValues, sortBy } from 'lodash';
import Keys from '@/constants/Keys';
import regionsObject from '@/config/odas/properties/regions.yml';
import statesObject from '@/config/odas/static/states.yml';
import ODAS from '@/utils/api/odas';
import { isBot } from '@/utils/utils';

Geocode.setApiKey(process.env.ODAS_GEOCODER_API_KEY);

const DEFAULT_REGIONS = {};
const REGION_RADIUS = 100; // miles
const METERS_PER_MILE = 1609.344; // meters
const CACHE_EXPIRATION = 60 * 60 * 1000; // 1 hour
const SOLUTION_KEYS = Object.keys(regionsObject);

let geolocationPermissionGranted = null;
let geolocationPermissionCheckPromise = null;

// Including the lat/lng as their long name to keep compatibility with how Angular adds them to localStorage.
// namedLocations was previously an array of arrays in Angular with set indicies for each datapoint -
// this has been upgraded to an array of objects for React for ease of use and clearer code.
// Additionally the locationName key is used rather than name.
const namedLocations = {};
SOLUTION_KEYS.forEach((solutionId) => {
  namedLocations[solutionId] = mapValues(regionsObject[solutionId], (region, regionCode) => {
    region.latitude = region.lat;
    region.longitude = region.lng;
    region.locationName = region.name;
    return region;
  });
});

const states = Object.keys(statesObject)
  .sort()
  .map((abbrv) => ({ state: statesObject[abbrv], stateKey: abbrv }));

const supportedRegions = {};
SOLUTION_KEYS.forEach((solutionId) => {
  supportedRegions[solutionId] = Object.keys(namedLocations[solutionId])
    .filter((locationId) => namedLocations[solutionId][locationId]?.main)
    .map((locationId) => ({ id: locationId, ...namedLocations[solutionId][locationId] }));
});

SOLUTION_KEYS.forEach((solutionId) => {
  Object.entries(regionsObject[solutionId]).forEach(([regionKey, regionData]) => {
    if (regionData.is_default) {
      DEFAULT_REGIONS[solutionId] = regionKey;
    }
  });
});

/**
 * Extract a particular component from a Google Maps place by passing in the place, the Google
 * name of the component you're looking for, and a boolean indicating whether you want the short or
 * long name. For example, to extract the long state name, use
 * getComponentFromPlace(place, 'administrative_area_level_1', true).
 */
const getComponentFromPlace = (place, componentType, getLong) => {
  if (place.address_components) {
    for (var i = 0; i < place.address_components.length; i++) {
      var component = place.address_components[i];
      if (component.types) {
        for (var j = 0; j < component.types.length; j++) {
          if (component.types[j] === componentType) {
            return getLong ? component.long_name : component.short_name;
          }
        }
      }
    }
  }
  return null;
};

const detectLocationFromWindow = (options = {}) => {
  return new Promise((resolve, reject) => {
    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        geolocationPermissionGranted = true;
        resolve(position);
      },
      (error) => {
        reject(error);
      },
      {
        timeout: options.timeout ? options.timeout : 10000
      }
    );
  });
};

const closestRegion = (solutionId, latLng, regionRadius, defaultRegion, regionList) => {
  let selectedRegion = defaultRegion;
  let regionFound = false;
  Object.keys(namedLocations[solutionId]).forEach((key) => {
    if (Array.isArray(regionList) && !regionList.includes(key)) return;
    let distanceThisLoc = google.maps.geometry.spherical.computeDistanceBetween(
      latLng,
      new google.maps.LatLng(namedLocations[solutionId][key].lat, namedLocations[solutionId][key].lng)
    );
    if (distanceThisLoc <= regionRadius * METERS_PER_MILE && !regionFound) {
      selectedRegion = key;
      regionFound = true;
    }
  });
  return selectedRegion;
};

const regionObjectFromId = (solutionId, id) => {
  return find(supportedRegions[solutionId], { id: id });
};

const countyFromPlace = (solutionId, place) => {
  var county;
  var regionFromPlace = find(supportedRegions[solutionId], (region) => {
    return region.name == place.formatted_address;
  });
  if (regionFromPlace) {
    county = regionFromPlace.county;
  } else {
    county = getComponentFromPlace(place, 'administrative_area_level_2', true) || '';
  }
  return county.replace('County', '').trim();
};

class LocationHelper {
  cacheExpiration = CACHE_EXPIRATION;

  constructor(solutionId) {
    this.solutionId = solutionId;
    this.defaultRegion = DEFAULT_REGIONS[solutionId];
  }

  detectedRegion() {
    return this.guessClosestRegion(REGION_RADIUS, this.defaultRegion);
  }

  guessClosestRegion(region_radius, default_region) {
    return new Promise((resolve, reject) => {
      const detectLocationFromIp = () => {
        axios
          .get(Keys.googleCloud.geolocationUrl)
          .then((response) => {
            if (response) {
              let currentLatLng = new google.maps.LatLng(
                response.cityLatLong.slice(0, response.cityLatLong.indexOf(',')),
                response.cityLatLong.slice(response.cityLatLong.indexOf(',') + 1)
              );
              resolve(closestRegion(this.solutionId, currentLatLng, region_radius, default_region));
            }
            resolve(default_region);
          })
          .catch((err) => {
            reject(err);
          });
      };

      if (isBot()) {
        reject({ message: 'bot detected' });
      }

      // // TODO use solution configuration to iterate over these paths
      // // These paths are not relevant to React (yet) but were part of this function that was ported directly from Angular
      // if (window.location.pathname === '/nyc') {
      //   resolve('nyc');
      // }
      // if (window.location.pathname === '/swnm') {
      //   resolve('swnm');
      // }
      // if (window.location.pathname === '/live6') {
      //   resolve('detroit');
      // }
      // if (window.location.pathname === '/lacountyassist') {
      //   resolve('la');
      // }

      this.checkGeolocationPermissions().then((permissionGranted) => {
        if (permissionGranted) {
          detectLocationFromWindow()
            .then((position) => {
              resolve(
                closestRegion(this.solutionId, new google.maps.LatLng(position.coords.latitude, position.coords.longitude)),
                region_radius,
                default_region
              );
            })
            .catch(detectLocationFromIp);
        } else {
          detectLocationFromIp();
        }
      });
    });
  }

  detectLocation() {
    return detectLocationFromWindow();
  }

  /**
   * Get the county name and chop off "County".
   * For some of our supported cities (NYC), Google does not return a county name
   * so first, try to find the county from supportedRegions
   * otherwise, try to get the County from the Google place object
   */
  getCountyFromPlace(place) {
    return countyFromPlace(this.solutionId, place);
  }

  getCurrentRegion() {
    return new Promise((resolve, reject) => {
      this.detectedRegion()
        .then((regionId) => {
          if (regionId) {
            resolve({ region: regionId, usingDefaultRegion: false });
          } else {
            throw new Error(); //trigger catch logic
          }
        })
        .catch((err) => {
          resolve({ region: this.defaultRegion, usingDefaultRegion: true });
        });
    });
  }

  getDetectedLocation() {
    return detectLocationFromWindow().then((position) => {
      return this.reverseGeocode(position.coords.latitude, position.coords.longitude);
    });
  }

  getNamedLocation(locationId) {
    return namedLocations[this.solutionId][locationId];
  }

  // determine default location for when there wasn't a location question in the assessment
  // if assessment is regional, in and currenty location isn't close to one of the supported regions,
  // set the default region to the center of the first enabled region
  determineAssessmentDefaultLocation(currentLocation, assessmentRegionList) {
    try {
      if (!assessmentRegionList || this.isLatLngInRegion(currentLocation, assessmentRegionList)) {
        return currentLocation;
      } else {
        return this.getRegionObjectFromId(closestRegion(this.solutionId, currentLocation, 1000, assessmentRegionList[0], assessmentRegionList));
      }
    } catch (error) {
      return this.getRegionObjectFromId(this.defaultRegion);
    }
  }

  getRegionByLatLng(lat, lng) {
    return ODAS.get('/api/closest/region', {
      lat: lat,
      lng: lng
    })
      .then((response) => {
        if (response.status === 200) {
          return { region: response.data.region, usingDefaultRegion: response.data.is_default };
        } else {
          throw new Error(); //trigger catch logic
        }
      })
      .catch((error) => {
        return { region: this.defaultRegion, usingDefaultRegion: true };
      });
  }

  getRegionObjectFromId(region) {
    return regionObjectFromId(this.solutionId, region);
  }

  /**
   * Get the long state name (eg "California") from a Google Maps place.
   */
  getStateFromPlace(place, longName = true) {
    return getComponentFromPlace(place, 'administrative_area_level_1', longName);
  }

  isLatLngInRegion(latLng, regionId) {
    if (namedLocations[this.solutionId][regionId]?.bounding_box?.length === 2) {
      return (
        latLng.lat < namedLocations[this.solutionId][regionId]?.bounding_box[0].lat &&
        latLng.lat > namedLocations[this.solutionId][regionId]?.bounding_box[1].lat &&
        latLng.lng > namedLocations[this.solutionId][regionId]?.bounding_box[0].lng &&
        latLng.lng < namedLocations[this.solutionId][regionId]?.bounding_box[1].lng
      );
    } else {
      return false;
    }
  }

  stateAbbreviationToName(abbrv) {
    if (typeof statesObject[abbrv.toUpperCase()] !== 'undefined') {
      return statesObject[abbrv.toUpperCase()];
    } else {
      return abbrv;
    }
  }

  getZipFromLatLngs(lat, lng) {
    return Geocode.fromLatLng(lat, lng).then(
      (response) => {
        const zip = response.results[0].address_components.filter((c) => c.types.includes('postal_code'))?.[0]?.short_name;
        return zip;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  getZipFromPlace(place) {
    return getComponentFromPlace(place, 'postal_code');
  }

  getLocationFromZipCode(zip) {
    // Google Maps Geocode doesn't return results if only a zip code is passed, append
    // the country to the string to receive results.
    // Seems safe to hard code this since to serve other countries, we'd need a lot of
    // code updates - adding a TODO below to help locate this if/when we consider supporting
    // another country.
    // TODO: International Support
    return this.getLocationFromAddressString(zip + ', US');
  }

  getLocationFromAddressString(address) {
    return Geocode.fromAddress(address).then((response) => {
      if (response.results && response.results.length) {
        return this.buildLocationFromPlace(response.results[0]);
      } else {
        throw new Error(`No results returned for getLocationFromAddressString("${address}")`);
      }
    });
  }

  checkGeolocationPermissions() {
    if (geolocationPermissionGranted !== null) {
      return new Promise((resolve) => {
        resolve(geolocationPermissionGranted);
      });
    } else if (geolocationPermissionCheckPromise !== null) {
      return geolocationPermissionCheckPromise;
    } else if (navigator.permissions && navigator.permissions.query) {
      geolocationPermissionCheckPromise = navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          geolocationPermissionGranted = result && result.state === 'granted';
          return geolocationPermissionGranted;
        })
        .catch((error) => {
          console.error(error);
          geolocationPermissionGranted = false;
          return geolocationPermissionGranted;
        });
      return geolocationPermissionCheckPromise;
    } else {
      geolocationPermissionGranted = false;
      return new Promise((resolve) => {
        resolve(geolocationPermissionGranted);
      });
    }
  }

  isGeolocationPermissionGranted() {
    return geolocationPermissionGranted;
  }

  reverseGeocode(lat, lng) {
    return Geocode.fromLatLng(lat, lng)
      .then((response) => {
        if (response.results && response.results.length) {
          return Object.assign(response.results[0], {
            lat: lat,
            lng: lng,
            latitude: lat,
            longitude: lng,
            locationName: response.results[0].formatted_address,
            state: this.getStateFromPlace(response.results[0]),
            county: this.getCountyFromPlace(response.results[0]),
            cachedAt: Date.now()
          });
        } else {
          throw new Error(`No results returned for ${lat}, ${lng}`);
        }
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  getSupportedRegionsOptionsList() {
    const regions = each(supportedRegions[this.solutionId], (region) => {
      const state_abbrv = region.name.split(', ')[1].slice(0, 2); // the first two characters here are the state abbreviation
      const region_name_short = region.region_name.split(',')[0]; // if the region name also contains the state, don't include it
      return (region.option_name = state_abbrv + ' - ' + region_name_short);
    });
    return sortBy(regions, 'option_name');
  }

  buildLocationFromPlace(place) {
    if (!place) return false;
    // If place comes from GooglePlace API (via Map or Autocomplete component), it has LatLng ({lat: fn(), lng: fun()}) interface for its geometry
    // However if place comes from Geocoder, it has LatLngLiteral ({lat: .., lng: ..}) interface for its geometry
    const lat = typeof place.geometry?.location?.lat == 'function' ? place.geometry.location.lat() : place.geometry?.location?.lat;
    const lng = typeof place.geometry?.location?.lat == 'function' ? place.geometry.location.lng() : place.geometry?.location?.lng;

    return {
      county: this.getCountyFromPlace(place),
      latitude: lat,
      longitude: lng,
      lat: lat,
      lng: lng,
      locationName: place.formatted_address,
      state: this.getStateFromPlace(place),
      zip: place.zip,
      city: getComponentFromPlace(place, 'locality', true),
      cachedAt: Date.now()
    };
  }

  isCountyFromCategoryMappings(categoryMappings, county) {
    return categoryMappings[county?.toLowerCase()] !== undefined;
  }
}

export default LocationHelper;
